/**
 * DOS518 — Integrazione ASTUTO
 * jsonParser.ts — Parser JSON per API REST e payload clipboard.
 * Gestisce due formati:
 *   (a) Oggetto diretto compatibile con AstutoIntervento
 *   (b) Envelope REST { data: {...}, status: "ok", ... }
 */

import type { AstutoIntervento, AstutoPersona, AstutoMezzo, AstutoMezzoAereo } from '../types';

export function parseJSON(content: string): AstutoIntervento {
  let raw: any;
  try {
    raw = JSON.parse(content);
  } catch {
    throw new Error('JSON non valido');
  }

  // Supporta envelope REST: { data: {...} } o { intervento: {...} } o diretto
  const src: any = raw?.data ?? raw?.intervento ?? raw;

  const intervento: AstutoIntervento = {
    codice:      src.codice      ?? src.CodiceIntervento ?? src.id    ?? undefined,
    data:        src.data        ?? src.DataIntervento   ?? undefined,
    ora:         src.ora         ?? src.OraIntervento    ?? undefined,
    comune:      src.comune      ?? src.Comune           ?? undefined,
    localita:    src.localita    ?? src.Localita         ?? undefined,
    latitudine:  String(src.latitudine  ?? src.lat ?? src.LAT ?? '').trim() || undefined,
    longitudine: String(src.longitudine ?? src.lon ?? src.LON ?? '').trim() || undefined,
    personale:       [],
    mezziTerrestri:  [],
    mezziAerei:      [],
  };

  // Personale — accetta array con diversi nomi di campo
  const personeRaw: any[] =
    src.personale ?? src.Personale ?? src.operatori ?? src.personnel ?? [];
  intervento.personale = personeRaw.map((p: any): AstutoPersona => ({
    matricola: p.matricola ?? p.Matricola ?? p.id    ?? undefined,
    nome:      p.nome      ?? p.Nome      ?? p.name  ?? '',
    cognome:   p.cognome   ?? p.Cognome   ?? p.surname ?? '',
    qualifica: p.qualifica ?? p.Qualifica ?? p.ruolo ?? undefined,
    telefono:  p.telefono  ?? p.Telefono  ?? p.cellulare ?? undefined,
    radio:     p.radio     ?? p.Radio     ?? p.canale    ?? undefined,
    ente:      p.ente      ?? p.Ente      ?? undefined,
  })).filter((p) => p.nome || p.cognome);

  // Mezzi Terrestri
  const mezziTRaw: any[] =
    src.mezziTerrestri ?? src.MezziTerrestri ?? src.mezzi_terrestri ?? src.vehicles ?? [];
  intervento.mezziTerrestri = mezziTRaw.map((m: any): AstutoMezzo => ({
    sigla: m.sigla ?? m.Sigla ?? m.codice ?? m.id ?? '',
    tipo:  m.tipo  ?? m.Tipo  ?? m.tipologia ?? undefined,
    targa: m.targa ?? m.Targa ?? undefined,
    ente:  m.ente  ?? m.Ente  ?? m.provenienza ?? undefined,
  })).filter((m) => m.sigla);

  // Mezzi Aerei
  const mezziARaw: any[] =
    src.mezziAerei ?? src.MezziAerei ?? src.mezzi_aerei ?? src.aircraft ?? [];
  intervento.mezziAerei = mezziARaw.map((m: any): AstutoMezzoAereo => ({
    sigla:   m.sigla   ?? m.Sigla   ?? m.codice ?? m.id ?? '',
    tipo:    m.tipo    ?? m.Tipo    ?? undefined,
    modello: m.modello ?? m.Modello ?? undefined,
  })).filter((m) => m.sigla);

  return intervento;
}
